import { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Space,
  Table,
  message,
  Checkbox,
  Tag,
  Tooltip,
} from 'antd';
import { DeleteOutlined, PlusOutlined, SaveOutlined, InfoCircleOutlined, PercentageOutlined } from '@ant-design/icons';
import { supabase } from '../lib/supabase';
import { Product } from '../types';
import { useProductPriceTiers } from '../hooks/useProductPriceTiers';
import PlacesAutocomplete from './PlacesAutocomplete';

interface SavedAddress {
  id: string;
  name: string;
  street: string;
  postal_code: string;
  city: string;
  country: string;
  is_default: boolean;
}

interface NewOrderDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (orderData: {
    products: Array<{
      product_id: string;
      hoeveelheid: number;
    }>;
    bezorgadres: string;
    bezorg_postcode: string;
    bezorg_stad: string;
    bezorg_land?: string;
    notities?: string;
    eigen_referentie?: string;
    gewenste_leverdatum?: string;
  }) => Promise<void>;
}

// Helper function to format price as currency
const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR'
  }).format(price);
};

export default function NewOrderDialog({ open, onClose, onSubmit }: NewOrderDialogProps) {
  const [form] = Form.useForm();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([]);
  const [showSaveAddressModal, setShowSaveAddressModal] = useState(false);
  const [saveAddressForm] = Form.useForm();
  const [selectedProducts, setSelectedProducts] = useState<{[key: string]: {product: Product, quantity: number}}>({});
  const [calculatingPrices, setCalculatingPrices] = useState(false);
  const [orderTotal, setOrderTotal] = useState(0);
  
  // Use our custom hook to get tiered pricing data
  const { priceTiers, getPriceForQuantity, loading: tiersLoading } = useProductPriceTiers();

  useEffect(() => {
    fetchProducts();
    fetchSavedAddresses();
  }, []);

  // Update order total whenever products or quantities change
  useEffect(() => {
    setCalculatingPrices(true);
    // Short delay to allow UI to update
    const timer = setTimeout(() => {
      setCalculatingPrices(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [selectedProducts]);

  // Force UI update when form values change
  const forceUpdate = () => {
    setCalculatingPrices(true);
    // Use a shorter timeout to make the UI more responsive
    setTimeout(() => {
      try {
        const newSelectedProducts = {...selectedProducts};
        setSelectedProducts(newSelectedProducts);
      } finally {
        setCalculatingPrices(false);
      }
    }, 50);
  };

  // Listen for form changes to update the selected products
  useEffect(() => {
    const formProducts = form.getFieldValue('products') || [];
    
    const newSelectedProducts: {[key: string]: {product: Product, quantity: number}} = {};
    
    formProducts.forEach((formProduct: any, index: number) => {
      if (formProduct && formProduct.product_id) {
        const product = products.find(p => p.id === formProduct.product_id);
        if (product) {
          newSelectedProducts[index] = {
            product,
            quantity: formProduct.quantity || 1
          };
        }
      }
    });
    
    setSelectedProducts(newSelectedProducts);
  }, [form.getFieldsValue(), products]);

  // Update the form when the fields change
  const handleProductChange = (value: string, index: number) => {
    const formData = form.getFieldsValue();
    const productData = formData.products || [];
    
    if (productData[index]) {
      productData[index].product_id = value;
      
      // Set appropriate minimum quantity based on MOQ and verkoopeenheid
      const product = products.find(p => p.id === value);
      if (product && product.moq && product.moq > 0) {
        const unit = product.artikel_per || 'stuk';
        let minQuantity = product.moq;
        
        // If unit is "1000 stuks", adjust the quantity calculation
        if (unit === '1000 stuks') {
          minQuantity = Math.ceil(product.moq / 1000);
        }
        
        productData[index].quantity = minQuantity;
      }
      
      form.setFieldsValue({ products: productData });
      updateOrderTotal();
      forceUpdate();
    }
  };

  const handleQuantityChange = (value: number | null, index: number) => {
    const formData = form.getFieldsValue();
    const productData = formData.products || [];
    
    if (productData[index]) {
      productData[index].quantity = value || 1; // Default to 1 if null
      form.setFieldsValue({ products: productData });
      updateOrderTotal();
      forceUpdate();
    }
  };

  // Helper function to update the order total
  const updateOrderTotal = () => {
    const formProducts = form.getFieldValue('products') || [];
    let total = 0;
    
    formProducts.forEach((item: any) => {
      if (item && item.product_id) {
        const quantity = item.quantity || 0;
        const price = getProductPrice(item.product_id, quantity);
        total += price * quantity;
      }
    });
    
    setOrderTotal(total);
  };

  const fetchSavedAddresses = async () => {
    try {
      const { data, error } = await supabase
        .from('saved_addresses')
        .select('*')
        .order('is_default', { ascending: false })
        .order('name');

      if (error) throw error;
      setSavedAddresses(data || []);
    } catch (error) {
      console.error('Error fetching saved addresses:', error);
      message.error('Er is een fout opgetreden bij het ophalen van de opgeslagen adressen');
    }
  };

  const handleSaveAddress = async (values: any) => {
    try {
      // Get the current logged-in user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        throw new Error('User not authenticated');
      }
      
      const { error } = await supabase
        .from('saved_addresses')
        .insert({
          user_id: user.id, // Explicitly set the user_id
          name: values.name,
          street: form.getFieldValue('shipping_address'),
          postal_code: form.getFieldValue('shipping_postcode'),
          city: form.getFieldValue('shipping_city'),
          country: form.getFieldValue('shipping_country'),
          is_default: values.is_default,
        });

      if (error) throw error;
      
      message.success('Adres succesvol opgeslagen');
      setShowSaveAddressModal(false);
      saveAddressForm.resetFields();
      fetchSavedAddresses();
    } catch (error) {
      console.error('Error saving address:', error);
      message.error('Er is een fout opgetreden bij het opslaan van het adres');
    }
  };

  const handleSelectSavedAddress = (addressId: string) => {
    const address = savedAddresses.find(addr => addr.id === addressId);
    if (address) {
      form.setFieldsValue({
        shipping_address: address.street,
        shipping_postcode: address.postal_code,
        shipping_city: address.city,
        shipping_country: address.country,
      });
    }
  };

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('producten')
        .select('*')
        .order('naam');

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      message.error('Er is een fout opgetreden bij het ophalen van de producten');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      const orderProducts = values.products || [];
      
      if (!orderProducts.length) {
        message.error('Voeg minimaal één product toe');
        return;
      }

      await onSubmit({
        products: orderProducts.map((item: any) => ({
          product_id: item.product_id,
          hoeveelheid: item.quantity,
        })),
        bezorgadres: values.shipping_address,
        bezorg_postcode: values.shipping_postcode,
        bezorg_stad: values.shipping_city,
        bezorg_land: values.shipping_country,
        notities: values.notes,
        eigen_referentie: values.eigen_referentie,
        gewenste_leverdatum: values.gewenste_leverdatum,
      });

      form.resetFields();
      onClose();
    } catch (error) {
      console.error('Error submitting order:', error);
      message.error('Er is een fout opgetreden bij het aanmaken van de order');
    } finally {
      setLoading(false);
    }
  };

  // Calculate the applicable tiered price for a product
  const getProductPrice = (productId: string, quantity: number): number => {
    const product = products.find(p => p.id === productId);
    if (!product) return 0;
    
    return getPriceForQuantity(productId, quantity, product.prijs);
  };

  // Get tier information for a product to display in tooltip
  const getProductTierInfo = (productId: string): React.ReactNode => {
    const productTiers = priceTiers.filter(tier => tier.product_id === productId)
      .sort((a, b) => a.min_quantity - b.min_quantity);
    
    if (productTiers.length === 0) return null;
    
    const product = products.find(p => p.id === productId);
    if (!product) return null;
    
    const unit = product.artikel_per || 'stuk';
    
    return (
      <div>
        <p><strong>Staffelprijzen:</strong></p>
        <p>Standaard: {formatPrice(product.prijs)} per {unit}</p>
        {productTiers.map(tier => (
          <p key={tier.id}>
            Vanaf {tier.min_quantity} {unit}: {formatPrice(tier.price)} per {unit}
          </p>
        ))}
      </div>
    );
  };

  const productColumns = [
    {
      title: 'Product',
      dataIndex: 'product_id',
      key: 'product_id',
      width: '25%',
      render: (_: any, record: any, index: number) => (
        <Form.Item
          name={[index, 'product_id']}
          rules={[{ required: true, message: 'Selecteer een product' }]}
          style={{ margin: 0 }}
        >
          <Select 
            style={{ width: '100%' }}
            placeholder="Selecteer een product"
            dropdownStyle={{ width: 'auto', minWidth: '400px' }}
            onChange={(value) => {
              // Update quantity to match MOQ if needed
              const product = products.find(p => p.id === value);
              if (product && product.moq && product.moq > 1) {
                // Set quantity to MOQ if it's specified and greater than 1
                const formProducts = form.getFieldValue('products') || [];
                if (formProducts[index]) {
                  formProducts[index].quantity = product.moq;
                  form.setFieldsValue({ products: formProducts });
                }
              }
              handleProductChange(value, index);
            }}
            optionLabelProp="label"
            showSearch
            filterOption={(input, option) => 
              (option?.title?.toLowerCase() || '').includes(input.toLowerCase()) ||
              (option?.searchvalue?.toLowerCase() || '').includes(input.toLowerCase())
            }
          >
            {products.map((product) => (
              <Select.Option 
                key={product.id} 
                value={product.id}
                title={`${product.naam} - ${product.artikelnummer || 'Geen artikelnr.'}`}
                searchvalue={`${product.naam} ${product.artikelnummer || ''}`}
                label={product.naam}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <span style={{ fontWeight: 'bold' }}>{product.naam}</span>
                  <span style={{ color: '#888', marginLeft: '10px' }}>
                    {product.artikelnummer ? `#${product.artikelnummer}` : 'Geen artikelnr.'}
                  </span>
                </div>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      ),
    },
    {
      title: 'Artikelnr.',
      key: 'artikelnummer',
      width: 110,
      render: (_: any, record: any, index: number) => {
        const productId = form.getFieldValue(['products', index, 'product_id']);
        if (!productId) return '-';
        
        const product = products.find(p => p.id === productId);
        if (!product) return '-';
        
        return product.artikelnummer || '-';
      }
    },
    {
      title: 'Verkoopeenheid',
      key: 'unit',
      render: (_: any, record: any, index: number) => {
        const productId = form.getFieldValue(['products', index, 'product_id']);
        if (!productId) return '-';
        
        const product = products.find(p => p.id === productId);
        if (!product) return '-';
        
        const unit = product.artikel_per || 'stuk';
        
        return (
          <Tooltip title={`Dit product wordt verkocht per ${unit}`}>
            <span style={{ 
              display: 'inline-block',
              padding: '2px 8px',
              background: '#f5f5f5',
              borderRadius: '4px',
              fontSize: '12px'
            }}>
              {unit}
            </span>
          </Tooltip>
        );
      }
    },
    {
      title: 'Aantal',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (_: any, record: any, index: number) => {
        const productId = form.getFieldValue(['products', index, 'product_id']);
        const product = products.find(p => p.id === productId);
        
        if (!product) {
          return (
            <Form.Item
              name={[index, 'quantity']}
              rules={[{ required: true, message: 'Voer een aantal in' }]}
              style={{ margin: 0 }}
              initialValue={1}
            >
              <InputNumber 
                min={1} 
                style={{ width: '80px' }}
                onChange={(value) => {
                  handleQuantityChange(value, index);
                }}
                step={1}
              />
            </Form.Item>
          );
        }
        
        // Calculate minimum order quantity based on verkoopeenheid
        let minQuantity = 1;
        const unit = product.artikel_per || 'stuk';
        
        if (product?.moq && product.moq > 0) {
          if (unit === '1000 stuks') {
            minQuantity = Math.ceil(product.moq / 1000);
          } else {
            minQuantity = product.moq;
          }
        }

        // Determine step and stringMode based on verkoopeenheid
        const isWholeNumberUnit = unit === '1000 stuks' || unit === 'stuk';
        const step = isWholeNumberUnit ? 1 : 0.1;
        const useStringMode = !isWholeNumberUnit; // Use stringMode for decimal inputs like kg
        
        return (
          <Form.Item
            name={[index, 'quantity']}
            rules={[
              { required: true, message: 'Voer een aantal in' },
              { 
                validator: (_, value) => {
                  if (value < minQuantity) {
                    return Promise.reject(`Minimale bestelhoeveelheid is ${minQuantity} ${unit === '1000 stuks' ? 'eenheden' : unit}`);
                  }
                  // For whole number units, ensure no decimals
                  if (isWholeNumberUnit && value % 1 !== 0) {
                    return Promise.reject(`Aantal moet een geheel getal zijn voor ${unit}`);
                  }
                  return Promise.resolve();
                }
              }
            ]}
            style={{ margin: 0 }}
            initialValue={minQuantity}
          >
            <InputNumber 
              min={minQuantity} 
              style={{ width: '80px' }}
              onChange={(value) => {
                handleQuantityChange(value, index);
              }}
              step={step}
              stringMode={useStringMode}
              precision={isWholeNumberUnit ? 0 : 1}
            />
          </Form.Item>
        );
      },
    },
    {
      title: 'Totaal aantal',
      key: 'total_quantity',
      render: (_: any, record: any, index: number) => {
        const productId = form.getFieldValue(['products', index, 'product_id']);
        const quantity = form.getFieldValue(['products', index, 'quantity']) || 1;
        
        if (!productId) return '-';
        
        const product = products.find(p => p.id === productId);
        if (!product) return '-';
        
        const unit = product.artikel_per || 'stuk';
        let totalQuantity = quantity;
        
        // Calculate the actual number of items
        if (unit === '1000 stuks') {
          totalQuantity = quantity * 1000;
          return (
            <Tooltip title={`${quantity} eenheden van ${unit}`}>
              <span>{totalQuantity.toLocaleString('nl-NL')} stuks</span>
            </Tooltip>
          );
        }
        
        return <span>{totalQuantity} {unit}</span>;
      }
    },
    {
      title: 'Prijs per eenheid',
      key: 'unit_price',
      render: (_: any, record: any, index: number) => {
        const productId = form.getFieldValue(['products', index, 'product_id']);
        const quantity = form.getFieldValue(['products', index, 'quantity']) || 1;
        
        if (!productId) return '-';
        
        const product = products.find(p => p.id === productId);
        if (!product) return '-';
        
        const unit = product.artikel_per || 'stuk';
        const basePrice = product.prijs;
        const actualPrice = getProductPrice(productId, quantity);
        const hasTiers = priceTiers.some(tier => tier.product_id === productId);
        
        // Show discount indicator if tiered pricing is applied
        const hasDiscount = actualPrice < basePrice;
        
        return (
          <div>
            {calculatingPrices && tiersLoading ? (
              <span style={{ opacity: 0.6 }}>Berekenen...</span>
            ) : (
              <>
                <div>
                  {formatPrice(actualPrice)}
                  {hasDiscount && (
                    <Tooltip title="Staffelkorting toegepast">
                      <PercentageOutlined style={{ 
                        marginLeft: 4, 
                        color: '#52c41a', 
                        fontSize: '13px',
                        position: 'relative',
                        top: '-1px' 
                      }} />
                    </Tooltip>
                  )}
                </div>
                <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
                  per {unit}
                </div>
                {hasTiers && (
                  <Tooltip title={getProductTierInfo(productId)}>
                    <InfoCircleOutlined style={{ 
                      marginLeft: 4, 
                      color: '#1890ff', 
                      fontSize: '12px',
                      position: 'relative',
                      top: '-1px' 
                    }} />
                  </Tooltip>
                )}
              </>
            )}
          </div>
        );
      },
    },
    {
      title: 'Totaal',
      key: 'total',
      render: (_: any, record: any, index: number) => {
        const productId = form.getFieldValue(['products', index, 'product_id']);
        const quantity = form.getFieldValue(['products', index, 'quantity']) || 1;
        
        if (!productId) return '-';
        
        const price = getProductPrice(productId, quantity);
        return calculatingPrices && tiersLoading ? (
          <span style={{ opacity: 0.6 }}>Berekenen...</span>
        ) : (
          <strong>{formatPrice(price * quantity)}</strong>
        );
      }
    },
    {
      title: 'Actie',
      key: 'action',
      render: (_: any, record: any, index: number, remove: (index: number) => void) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => remove(index)}
        />
      ),
    },
  ];

  // Calculate total price whenever form values change
  useEffect(() => {
    const formProducts = form.getFieldValue('products') || [];
    let total = 0;
    
    formProducts.forEach((item: any) => {
      if (item && item.product_id) {
        const quantity = item.quantity || 0;
        const price = getProductPrice(item.product_id, quantity);
        total += price * quantity;
      }
    });
    
    setOrderTotal(total);
  }, [selectedProducts, priceTiers, products]);
  
  // Update summary display
  const renderSummary = () => {
    return (
      <Table.Summary.Row style={{ fontWeight: 'bold' }}>
        <Table.Summary.Cell index={0} colSpan={6}>
          <strong>Order Totaal</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={1}>
          <strong style={{ fontSize: '16px', color: '#1890ff' }}>{formatPrice(orderTotal)}</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={2}></Table.Summary.Cell>
      </Table.Summary.Row>
    );
  };

  return (
    <Modal
      title="Nieuwe order"
      open={open}
      onCancel={onClose}
      footer={null}
      width={1200}
      styles={{
        body: { overflowX: 'auto' }
      }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          products: [],
          shipping_country: 'Nederland',
        }}
      >
        <Form.Item
          name="saved_address"
          label="Opgeslagen adres"
          style={{ marginBottom: 8 }}
        >
          <Select
            placeholder="Selecteer een opgeslagen adres"
            onChange={handleSelectSavedAddress}
            allowClear
          >
            {savedAddresses.map((address) => (
              <Select.Option key={address.id} value={address.id}>
                {address.name} - {address.street}, {address.postal_code} {address.city}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="shipping_address"
          label="Bezorgadres"
          rules={[{ required: true, message: 'Voer een bezorgadres in' }]}
        >
          <PlacesAutocomplete 
            onSelect={(address) => {
              form.setFieldsValue({
                shipping_address: address.street,
                shipping_postcode: address.postalCode,
                shipping_city: address.city,
                shipping_country: address.country,
              });
            }} 
            skipAutocompleteOnChange={true}
          />
        </Form.Item>

        <Form.Item
          name="shipping_postcode"
          label="Postcode"
          rules={[{ required: true, message: 'Voer een postcode in' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="shipping_city"
          label="Stad"
          rules={[{ required: true, message: 'Voer een stad in' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="shipping_country"
          label="Land"
          initialValue="Nederland"
        >
          <Input />
        </Form.Item>

        <Form.Item>
          <Button
            type="dashed"
            icon={<SaveOutlined />}
            onClick={() => setShowSaveAddressModal(true)}
          >
            Adres opslaan
          </Button>
        </Form.Item>

        <Form.Item
          name="gewenste_leverdatum"
          label="Gewenste leverdatum"
        >
          <Input type="date" />
        </Form.Item>

        <Form.Item 
          name="eigen_referentie" 
          label="Eigen referentie"
          style={{ marginBottom: 24 }}
        >
          <Input placeholder="Voer uw eigen referentie in (optioneel)" />
        </Form.Item>

        <Form.Item 
          name="notes" 
          label="Notities"
          style={{ marginBottom: 24 }}
        >
          <Input.TextArea rows={4} />
        </Form.Item>

        <Form.List name="products">
          {(fields, { add, remove }) => (
            <>
              <Form.Item label="Producten">
                <Table
                  dataSource={fields as any[]}
                  columns={productColumns.map(col => ({
                    ...col,
                    render: (...args) => col.render?.(...args, remove),
                  }))}
                  pagination={false}
                  summary={renderSummary}
                  scroll={{ x: 'max-content' }}
                  locale={{
                    emptyText: 'Geen producten toegevoegd. Klik op "Product toevoegen" hieronder om te beginnen.'
                  }}
                />
                <Button
                  type="dashed"
                  onClick={() => {
                    add({ quantity: 1 });
                    // Wait for the UI to update, then focus on the new select
                    setTimeout(() => {
                      const selects = document.querySelectorAll('.ant-select');
                      if (selects && selects.length > 0) {
                        const lastSelect = selects[selects.length - 1];
                        if (lastSelect) {
                          (lastSelect as HTMLElement).click();
                        }
                      }
                    }, 100);
                  }}
                  style={{ marginTop: 16, marginBottom: 24, width: '100%', height: '40px' }}
                  icon={<PlusOutlined />}
                >
                  Product toevoegen
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>

        <Form.Item>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={onClose}>
              Annuleren
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Order aanmaken
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Modal
        title="Adres opslaan"
        open={showSaveAddressModal}
        onCancel={() => setShowSaveAddressModal(false)}
        footer={null}
      >
        <Form
          form={saveAddressForm}
          layout="vertical"
          onFinish={handleSaveAddress}
        >
          <Form.Item
            name="name"
            label="Naam voor dit adres"
            rules={[{ required: true, message: 'Voer een naam in voor dit adres' }]}
          >
            <Input placeholder="Bijvoorbeeld: Thuis, Kantoor, etc." />
          </Form.Item>

          <Form.Item
            name="is_default"
            valuePropName="checked"
          >
            <Checkbox>Dit is mijn standaard adres</Checkbox>
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setShowSaveAddressModal(false)}>
                Annuleren
              </Button>
              <Button type="primary" htmlType="submit">
                Opslaan
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Modal>
  );
}
